import { Formio } from '@formio/js'
import { user } from "../app"
import { map } from "../constants"
import { getAudioDuration, serverUrl, uploadFileToTemp } from "../server/advayta"
import { deleteByPath } from "../upload"

const HtmlComponent = Formio.Components.components.container

class AudioListComponent extends HtmlComponent {
    /**
     * Определяет схему компонента.
     * @returns {Object} Схема компонента.
     */
    schema() {
        return {
            type: 'audiolist',
            key: 'audiolist',
            label: 'Аудио ссылки',
            addAnotherPosition: 'top',
            input: true,
            components: [],
        }
    }

    /**
     * Информация о компоненте для построителя.
     * @returns {Object} Данные для отображения компонента в библиотеке Formio.
     */
    get builderInfo() {
        return {
            title: 'Список аудио ссылок',
            group: 'custom',
            icon: 'headphones',
            weight: 0,
            documentation: 'https://example.com/docs/custom-components#audiolinklist',
            schema: this.schema(),
        }
    }

    /**
     * Конструктор компонента.
     * @param {Object} component Конфигурация компонента.
     * @param {Object} options Опции компонента.
     * @param {Object} data Данные для инициализации компонента.
     */
    constructor(component, options, data) {
        super(component, options, data)
        this.rows = []
        this.filesToDelete = [] // Массив для хранения файлов, которые нужно удалить
        this.originalValues = [] // Исходные значения аудиофайлов
        this.addRow = this.addRow.bind(this)
        this.removeRow = this.removeRow.bind(this)
        this.components = []
    }

    /**
     * Инициализация компонента.
     */
    init() {
        super.init()
        this.rows = []
        this.originalValues = JSON.parse(JSON.stringify(this.rows)) // Глубокая копия исходных значений
    }

    /**
     * Генерирует HTML-код компонента.
     * @returns {string} HTML-компонент.
     */
    render() {
        return super.render(this.renderTemplate())
    }

    /**
     * Генерирует шаблон для отображения компонента.
     * @returns {string} HTML-шаблон.
     */
    renderTemplate() {
        let rows = this.rows || []
        const isEditor = user && user.role === map.roles.editor
        const addButtonVisibility = isEditor ? '' : 'd-none'

        return `
            <div>
                <div class="d-flex align-items-center justify-content-between">
                     <label>Список аудио</label>
                     <button class="btn btn-primary btn-sm add-row-btn ${addButtonVisibility}">
                        <i class="fa fa-plus"></i> Добавить аудио
                     </button>
                </div>
                <div class="audio-link-list">
                    ${rows.map((row, index) => this.renderRow(row, index)).join('')}
                </div>
            </div>
        `
    }

    /**
     * Генерирует HTML-шаблон для строки аудио.
     * @param {Object} row Данные строки.
     * @param {number} index Индекс строки.
     * @returns {string} HTML-код строки.
     */
    renderRow(row, index) {
        if (typeof row.duration === 'number') {
            row.duration = this.secondsToHMS(row.duration)
        }
        document.addEventListener('input', (e) => {
            if (e.target.classList.contains('audio-url')) {
                const index = e.target.closest('.audio-link-row').querySelector('.upload-file-btn').dataset.index
                const url = e.target.value.trim()
                const audioWrapper = document.getElementById(`myElement-${index}`)
                if (url) {
                    audioWrapper.classList.remove('d-none')
                    audioWrapper.querySelector('source').src = url
                    audioWrapper.querySelector('audio').load()
                } else {
                    audioWrapper.classList.add('d-none')
                }
            }
        })

        const isEditor = user && user.role === map.roles.editor
        const buttonVisibility = isEditor ? '' : 'd-none'

        return `
            <div class="audio-link-row">
                <div class="d-flex gap-2">
                    <input type="text" class="form-control audio-chapter" value="${row.chapter || ''}" placeholder="Глава" />
                    <input type="text" class="form-control audio-url" value="${row.url || ''}" placeholder="Ссылка на аудио" />
                    <button class="btn btn-secondary btn-sm upload-file-btn ${buttonVisibility}" style="min-width: max-content" data-index="${index}">Загрузить файл</button>
                </div>
                <div style="font-size: 14px; color: green; display: none" class="file-uploading">Загрузка файла...</div>
                <input type="text" class="form-control audio-duration" value="${row.duration}" placeholder="Продолжительность" readonly/>
                <div id="myElement-${index}" class="${row.url ? '' : 'd-none'}">
                    <audio style="width: 100%;height: 30px;" controls>
                        <source src="${row.url || ''}" type="audio/ogg">
                    </audio>
                </div>
                <button class="btn btn-danger btn-sm remove-row-btn ${buttonVisibility}" data-index="${index}">
                    Удалить
                </button>
            </div>
        `
    }

    /**
     * Привязывает обработчики событий к элементам DOM.
     * @param {HTMLElement} element Элемент DOM, к которому нужно привязать события.
     */
    attach(element) {
        super.attach(element)
        this.addRowButton = element.querySelector('.add-row-btn')
        this.addRowButton.addEventListener('click', this.addRow)

        // Привязываем события к существующим строкам
        const rows = element.querySelectorAll('.audio-link-row')
        rows.forEach((row, index) => {
            this.attachEventsToRow(row)
        })

        this.inputs = element.querySelectorAll('input')
        this.inputs.forEach((input) => {
            input.addEventListener('change', () => {
                this.updateValue()
            })
        })
    }

    /**
     * Удаляет обработчики событий из элементов DOM.
     */
    detach() {
        if (this.addRowButton) {
            this.addRowButton.removeEventListener('click', this.addRow)
        }

        if (this.inputs) {
            this.inputs.forEach((input) => {
                input.removeEventListener('change', () => {
                    this.updateValue()
                })
            })
        }

        super.detach()
    }

    /**
     * Добавляет новую строку в компонент.
     */
    addRow() {
        if (this.rows && this.rows.length && this.rows[this.rows.length - 1].url === '') return
        this.rows.push({ url: '', chapter: '', duration: 'ЧЧ:ММ:CC' })
        const newRow = document.createElement('div')
        newRow.innerHTML = this.renderRow(this.rows[this.rows.length - 1], this.rows.length - 1)
        const container = this.element.querySelector('.audio-link-list')
        container.appendChild(newRow)
        this.attachEventsToRow(newRow)
        this.updateValue()
    }

    /**
     * Удаляет строку из компонента по индексу.
     * @param {number} index Индекс удаляемой строки.
     */
    removeRow(index) {
        const rowToRemove = this.rows[index]
        if (rowToRemove && rowToRemove.url) {
            // Добавляем файл в список для удаления при сохранении
            this.filesToDelete.push(rowToRemove.url)
        }

        this.rows.splice(index, 1)
        const container = this.element.querySelector('.audio-link-list')
        const rowElement = container.children[index]
        if (rowElement) {
            container.removeChild(rowElement)
        }

        // Обновляем индексы оставшихся кнопок
        Array.from(container.children).forEach((row, i) => {
            const removeBtn = row.querySelector('.remove-row-btn')
            const uploadBtn = row.querySelector('.upload-file-btn')
            if (removeBtn) removeBtn.dataset.index = i
            if (uploadBtn) uploadBtn.dataset.index = i
        })

        this.updateValue()
        this.recalculateDuration()
    }

    /**
     * Привязывает обработчики событий к новой строке.
     * @param {HTMLElement} row Элемент строки.
     */
    attachEventsToRow(row) {
        const urlInput = row.querySelector('.audio-url')
        const chapterInput = row.querySelector('.audio-chapter')
        const removeButton = row.querySelector('.remove-row-btn')
        const uploadButton = row.querySelector('.upload-file-btn')
        if (urlInput) {
            urlInput.addEventListener('change', this.updateValue.bind(this))
        }
        if (chapterInput) {
            chapterInput.addEventListener('change', this.updateValue.bind(this))
        }
        if (removeButton) {
            removeButton.addEventListener('click', (event) => {
                event.preventDefault()
                const index = parseInt(removeButton.dataset.index, 10)
                this.removeRow(index)
            })
        }
        if (uploadButton) {
            uploadButton.addEventListener('click', this.uploadFile.bind(this))
        }
    }

    /**
     * Возвращает текущее значение компонента.
     * @returns {Array} Массив объектов с данными строк.
     */
    getValue() {
        this.updateValue()
        return this.dataValue || []
    }

    updateAudioPlayer(index, url) {
        const audioWrapper = document.getElementById(`myElement-${index}`)
        if (url) {
            audioWrapper.classList.remove('d-none')
            audioWrapper.querySelector('source').src = url
            audioWrapper.querySelector('audio').load()
        } else {
            audioWrapper.classList.add('d-none')
        }
    }

    /**
     * Обновляет текущее значение компонента.
     * @returns {Array} Массив объектов с обновленными данными строк.
     */
    updateValue() {
        const rows = Array.from(this.element.querySelectorAll('.audio-link-row')).map(
            (row, index) => {
                const url = row.querySelector('.audio-url').value
                const audioWrapper = document.getElementById(`myElement-${index}`)
                if (audioWrapper) {
                    audioWrapper.classList.toggle('d-none', !url)
                    if (url) {
                        audioWrapper.querySelector('source').src = url
                        audioWrapper.querySelector('audio').load()
                    }
                }
                return {
                    url,
                    chapter: row.querySelector('.audio-chapter').value,
                    duration: row.querySelector('.audio-duration').value,
                }
            }
        )
        this.dataValue = rows
        return this.dataValue
    }

    /**
     * Устанавливает новое значение для компонента.
     * @param {Array} value Массив объектов, содержащих новые данные строк.
     */
    setValue(value) {
        this.dataValue = value
        this.rows = Array.isArray(this.dataValue) ? [...this.dataValue] : []
        this.redraw()
    }

    /**
     * Обрабатывает загрузку файла.
     * @param {Event} event Событие клика на кнопку загрузки файла.
     */
    uploadFile(event) {
        event.preventDefault()
        const index = parseInt(event.target.dataset.index, 10)
        const fileInput = document.createElement('input')
        fileInput.type = 'file'
        fileInput.accept = '.mp3'
        fileInput.addEventListener('change', async (fileEvent) => {
            const file = fileEvent.target.files[0]
            if (file) {
                try {
                    const currentUrl = this.rows[index]?.url
                    // Если есть текущий файл, добавляем его в список для удаления
                    if (currentUrl) {
                        this.filesToDelete.push(currentUrl)
                    }

                    event.target.closest('.audio-link-row').querySelector('.file-uploading').style.display = 'block'
                    const upload = await uploadFileToTemp(file)

                    if (upload) {
                        const audioUrl = `${serverUrl}/upload/${upload[0].name}`
                        this.rows[index].url = audioUrl

                        // Обновляем соответствующее поле
                        const urlInput = this.element.querySelectorAll('.audio-url')[index]
                        urlInput.value = audioUrl

                        this.updateValue()

                        getAudioDuration(audioUrl).then(result => {
                            this.rows[index].duration = result
                            const durationInput = this.element.querySelectorAll('.audio-duration')[index]
                            durationInput.value = this.secondsToHMS(result || 0)
                            this.updateValue()
                            this.recalculateDuration()
                        })
                    } else {
                        console.error('Ошибка при загрузке файла')
                    }
                } catch (error) {
                    console.error('Ошибка при загрузке файла:', error)
                } finally {
                    event.target.closest('.audio-link-row').querySelector('.file-uploading').style.display = 'none'
                }
            }
        })

        fileInput.click()
    }

    /**
     * Вызывается перед сохранением для удаления старых файлов
     */
    async processFilesToDelete() {
        try {
            // Находим файлы, которые были в оригинальных значениях, но отсутствуют сейчас
            this.originalValues.forEach(original => {
                if (original.url && !this.rows.some(r => r.url === original.url)) {
                    this.filesToDelete.push(original.url)
                }
            })

            // Удаляем все файлы из списка
            await Promise.all(
                this.filesToDelete.map(filePath =>
                    deleteByPath(filePath).catch(error =>
                        console.error(`Ошибка удаления файла ${filePath}:`, error))
                )
            )

            // Сбрасываем состояние
            this.filesToDelete = []
            this.originalValues = JSON.parse(JSON.stringify(this.rows))

        } catch (error) {
            console.error('Ошибка при обработке файлов для удаления:', error)
        }
    }

    async recalculateDuration() {
        const duration = this.rows.reduce((acc, current) => acc + this.HMSToSeconds(current.duration), 0)
        this.parent.getComponent('duration').setValue(this.secondsToHMS(duration))
    }

    secondsToHMS(seconds) {
        seconds = Number(seconds)

        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = Math.floor(seconds % 60)

        const pad = (num) => num.toString().padStart(2, '0')

        return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`
    }

    HMSToSeconds(hms) {
        if (typeof hms === 'number') {
            return hms
        }
        const parts = hms.split(':')
        if (parts.length !== 3) return 0

        const hours = parseInt(parts[0], 10) || 0
        const minutes = parseInt(parts[1], 10) || 0
        const seconds = parseInt(parts[2], 10) || 0

        return hours * 3600 + minutes * 60 + seconds
    }
}

export default AudioListComponent